import { CalendarDays } from 'lucide-react'
import { useState, type ReactElement } from 'react'
import type { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { parseLocalDate, toDateOnly } from '@/lib/date'

export interface IntervaloDatasProps {
  inicio?: string
  fim?: string
  onAplicar: (inicio: string, fim: string) => void
}

function rotulo(inicio?: string, fim?: string): string {
  if (!inicio || !fim) return 'Período personalizado'
  const fmt = (iso: string): string => {
    const [ano, mes, dia] = iso.split('-')
    return `${dia}/${mes}/${ano}`
  }
  return `${fmt(inicio)} → ${fmt(fim)}`
}

/**
 * Intervalo livre de datas. As datas são tratadas como data pura (`YYYY-MM-DD`)
 * via `@/lib/date` — `new Date(iso)` interpretaria em UTC e, em BRT, mostraria
 * o dia anterior.
 */
export function IntervaloDatas({ inicio, fim, onAplicar }: IntervaloDatasProps): ReactElement {
  const [aberto, setAberto] = useState(false)
  const [intervalo, setIntervalo] = useState<DateRange | undefined>(
    inicio && fim ? { from: parseLocalDate(inicio), to: parseLocalDate(fim) } : undefined,
  )

  const completo =
    intervalo?.from && intervalo.to ? { de: intervalo.from, ate: intervalo.to } : null

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="min-h-[44px] gap-2 sm:min-h-0">
          <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
          {rotulo(inicio, fim)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar mode="range" numberOfMonths={2} selected={intervalo} onSelect={setIntervalo} />
        <div className="border-border flex items-center justify-between gap-3 border-t p-3">
          <span className="text-muted-foreground text-xs">
            {completo ? 'Clique em aplicar para filtrar' : 'Escolha a data inicial e a final'}
          </span>
          {completo ? (
            <Button
              type="button"
              size="sm"
              className="min-h-[44px] sm:min-h-0"
              onClick={() => {
                onAplicar(toDateOnly(completo.de), toDateOnly(completo.ate))
                setAberto(false)
              }}
            >
              Aplicar
            </Button>
          ) : (
            <Button type="button" size="sm" className="min-h-[44px] sm:min-h-0" disabled>
              Aplicar
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
